import { FC, useEffect, useRef, useState } from 'react'
import React from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Card } from 'components'
import { useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import {
  AddAnalyzeCREServices,
  RequestGetAllCRE,
  ResponseAnalyseCRE
} from 'services/NGT/AnalyzeCRE'
import { FORMAT, SPACING, OLDYUP as YUP } from 'utils'
import * as Yup from 'yup'

import { ViewContractModal } from '../ViewContractModal/ViewContractModal'
import { FilterAnalyzeCre, TableAnalyzeCre } from './components'

export interface ItemCarteiras {
  selected: boolean
  content: ResponseAnalyseCRE
}

const DEFAULT_FORM: RequestGetAllCRE = {
  BloqCre: null,
  BloqPagamento: null,
  CRE: null,
  Contrato: null,
  DataFimLiberacao: null,
  DataFimRetorno: null,
  DataInicioLiberacao: null,
  DataInicioRetorno: null,
  MesAnoVerificacao: null,
  NmFornecedor: null,
  pageNumber: 1,
  pageSize: 15
}

const AnalyzeCre: FC = () => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [showContractModal, toggleContractModal] = useToggle()
  const isFirstMount = useRef<boolean>(true)
  const [contractID, setContractID] = useState<number>(0)

  const schema = Yup.object({
    MesAnoVerificacao: Yup.date().nullable().typeError('Data inválida')
  })
    .shape({})
    .concat(
      YUP.dateRange('DataInicioLiberacao', 'DataFimLiberacao').oneRequireAnother
    )
    .concat(
      YUP.dateRange('DataInicioRetorno', 'DataFimRetorno').oneRequireAnother
    )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors }
  } = useForm<RequestGetAllCRE>({
    defaultValues: DEFAULT_FORM,
    resolver: yupResolver(schema)
  })

  usePersistForm('analise-cre-ngt-form', {
    setValue,
    watch
  })

  const [listaCarteiras, setListaCarteiras] = useState<
    ItemCarteiras[] | undefined
  >()

  const [page, setPage] = React.useState<number>(1)
  const [rowSize, setRowSize] = React.useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(15)
  let fromPesquisar = false

  const dataInicioLiberacao = watch('DataInicioLiberacao')
  const dataFimLiberacao = watch('DataFimLiberacao')
  const dataInicioRetorno = watch('DataInicioRetorno')
  const dataFimRetorno = watch('DataFimRetorno')
  const mesAnoVerificacao = watch('MesAnoVerificacao')

  const areDatesValid = () => {
    const datesValicacao =
      dataInicioLiberacao || dataFimLiberacao
        ? moment(dataInicioLiberacao).isValid() &&
          moment(dataFimLiberacao).isValid() &&
          moment(dataFimLiberacao).isSameOrAfter(dataInicioLiberacao)
        : true

    const datesRetorno =
      dataInicioRetorno || dataFimRetorno
        ? moment(dataInicioRetorno).isValid() &&
          moment(dataFimRetorno).isValid() &&
          moment(dataFimRetorno).isSameOrAfter(dataInicioRetorno)
        : true

    const dateVerificacao = mesAnoVerificacao
      ? moment(mesAnoVerificacao).isValid()
      : true

    return datesValicacao && datesRetorno && dateVerificacao
  }

  const getData = async () => {
    setLoading('loading')

    if (fromPesquisar) {
      setPage(1)
    }

    const formData: RequestGetAllCRE = getValues()

    try {
      const response = await AddAnalyzeCREServices().searchCre({
        ...formData,
        ...(formData?.DataFimLiberacao && {
          DataFimLiberacao: FORMAT.formatYYYYMMDD2(formData?.DataFimLiberacao)
        }),
        ...(formData?.DataFimRetorno && {
          DataFimRetorno: FORMAT.formatYYYYMMDD2(formData?.DataFimRetorno)
        }),

        ...(formData?.MesAnoVerificacao && {
          MesAnoVerificacao: FORMAT.formatYYYYMM(formData?.MesAnoVerificacao)
        }),
        ...(formData?.DataInicioLiberacao && {
          DataInicioLiberacao: FORMAT.formatYYYYMMDD2(
            formData?.DataInicioLiberacao
          )
        }),
        ...(formData?.DataInicioRetorno && {
          DataInicioRetorno: FORMAT.formatYYYYMMDD2(formData?.DataInicioRetorno)
        }),
        pageNumber: fromPesquisar ? 1 : page,
        pageSize: rowsPerPage
      })

      if (response.status !== 200) {
        throw new Error('Erro')
      }

      setListaCarteiras(
        response.data.entity.items.map((item) => ({
          content: item,
          selected: false
        }))
      )

      setRowSize(response.data.entity.pageCount)
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: 'Não foi possivel obter dados',
        txtButtonConfirm: 'Fechar',
        type: 'alert'
      })
    }
  }

  const handleReset = () => {
    reset()
    setValue('Contrato', '')
    setValue('NmFornecedor', '')
    setValue('CRE', '')
    setPage(1)
    setRowsPerPage(15)
    if (page === 1) {
      getData()
    }
  }

  useEffect(() => {
    if (!fromPesquisar && !isFirstMount.current) {
      getData()
    } else {
      fromPesquisar = false
      isFirstMount.current = false
    }
  }, [page, rowsPerPage])

  useEffect(() => {
    const subs = watch(() => trigger())
    return () => subs.unsubscribe()
  }, [watch])

  const handleVerContrato = (idContrato: number) => {
    setContractID(idContrato)
    toggleContractModal()
  }

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FilterAnalyzeCre
          control={control}
          errors={errors}
          areDatesValid={areDatesValid}
          onSubmit={handleSubmit(() => {
            fromPesquisar = true
            getData()
          })}
          handleReset={handleReset}
        />
        <TableAnalyzeCre
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          setPage={setPage}
          areDatesValid={areDatesValid}
          rowsSize={rowSize}
          page={page}
          tableData={listaCarteiras}
          handleVerContrato={handleVerContrato}
        />
      </Card>
      <ViewContractModal
        contractID={contractID}
        showModal={showContractModal}
        toggleModal={toggleContractModal}
      />
    </>
  )
}

export default AnalyzeCre
